import { appNow } from "./appClock";
import { DEFAULT_INSPECTION_INTERVAL_HOURS, hydrateSceneryAirport, lookupAirport } from "./airports";
import { isHawker800xpType, seedHawkerInspections } from "./hawker800xp";
import { suggestDeparture } from "./clock";
import { radioIdFor } from "./radio";
import { computeLeg } from "./generator";
import { withLegs } from "./schedule";
import { pickPreferredHours } from "./dataSource";
import { isFuelUnit } from "./fuelUnits";
import { hasGroundingMel } from "./mel";
import type {
  AircraftSpec,
  AirframeStatus,
  DeskRole,
  DraftMission,
  FlightLogEntry,
  MelItem,
  CompanyMxBase,
  Mission,
  MxInspection,
  SceneryAirport,
  Squawk,
  SquawkSeverity,
} from "./types";

export { DEFAULT_INSPECTION_INTERVAL_HOURS };

export function inspectionDue(spec: AircraftSpec): boolean {
  return spec.airframeHours >= spec.nextInspectionDueHours;
}

export function hoursToInspection(spec: AircraftSpec): number {
  return Math.round((spec.nextInspectionDueHours - spec.airframeHours) * 10) / 10;
}

export function hydrateInspection(raw: Partial<MxInspection>, fallbackDue: number): MxInspection | null {
  const label = typeof raw.label === "string" ? raw.label.trim() : "";
  const dueHours = Number.isFinite(raw.dueHours) ? (raw.dueHours as number) : fallbackDue;
  if (!label) return null;
  const intervalHours =
    Number.isFinite(raw.intervalHours) && (raw.intervalHours as number) > 0
      ? (raw.intervalHours as number)
      : DEFAULT_INSPECTION_INTERVAL_HOURS;
  const intervalMonths =
    Number.isFinite(raw.intervalMonths) && (raw.intervalMonths as number) > 0
      ? Math.round(raw.intervalMonths as number)
      : undefined;
  return {
    id: raw.id || `insp-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    label,
    intervalHours,
    dueHours,
    lastCompletedHours: Number.isFinite(raw.lastCompletedHours) ? raw.lastCompletedHours : undefined,
    intervalMonths,
  };
}

export function extraInspectionsDue(spec: AircraftSpec): MxInspection[] {
  return (spec.inspections ?? []).filter((i) => spec.airframeHours >= i.dueHours);
}

export function anyInspectionOverdue(spec: AircraftSpec): boolean {
  return inspectionDue(spec) || extraInspectionsDue(spec).length > 0;
}

export function openSquawksFor(aircraftId: string, squawks: Squawk[]): Squawk[] {
  return squawks.filter((s) => s.aircraftId === aircraftId && s.status === "open");
}

export function hasOpenGroundSquawk(aircraftId: string, squawks: Squawk[]): boolean {
  return openSquawksFor(aircraftId, squawks).some((s) => s.severity === "ground");
}

/**
 * Inspection overdue, open ground-squawk, or grounding MEL forces grounded
 * unless already ferry / in_mx. Derived — not sticky after MEL close.
 */
export function resolveAirframeStatus(
  spec: AircraftSpec,
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): AirframeStatus {
  if (spec.status === "ferry" || spec.status === "in_mx") return spec.status;
  if (anyInspectionOverdue(spec) || hasOpenGroundSquawk(spec.id, squawks) || hasGroundingMel(spec.id, melItems)) {
    return "grounded";
  }
  return "available";
}

export function withResolvedStatus(
  spec: AircraftSpec,
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): AircraftSpec {
  const status = resolveAirframeStatus(spec, squawks, melItems);
  return { ...spec, status, available: status === "available" };
}

export function refreshFleetStatus(
  fleet: AircraftSpec[],
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): AircraftSpec[] {
  return fleet.map((a) => withResolvedStatus(a, squawks, melItems));
}

/**
 * Charter auto-assign / Inbox list — skip grounded, ferry, in_mx.
 * With no extra arrays, trusts the last refreshed `spec.status` (store keeps MEL in sync).
 */
export function isCharterAssignable(
  spec: AircraftSpec,
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): boolean {
  if (squawks.length || melItems.length) {
    return resolveAirframeStatus(spec, squawks, melItems) === "available";
  }
  if (spec.status === "ferry" || spec.status === "in_mx") return false;
  if (anyInspectionOverdue(spec)) return false;
  return spec.status === "available";
}

export function assignableFleet(
  fleet: AircraftSpec[],
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): AircraftSpec[] {
  return fleet.filter((a) => isCharterAssignable(a, squawks, melItems));
}

/** Keep a typed performance figure. Missing, zero, and junk stay unset. */
function optionalPerf(raw: unknown, cap: number, places = 0): number | undefined {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return undefined;
  const clamped = Math.min(raw, cap);
  const factor = 10 ** places;
  return Math.round(clamped * factor) / factor;
}

export function hydrateAircraft(raw: AircraftSpec): AircraftSpec {
  const airframeHours = Number.isFinite(raw.airframeHours) ? raw.airframeHours : 0;
  // Due at hour zero is the old blank-tail default, not a real inspection.
  const nextInspectionDueHours =
    Number.isFinite(raw.nextInspectionDueHours) && raw.nextInspectionDueHours > 0
      ? raw.nextInspectionDueHours
      : Math.round((airframeHours + DEFAULT_INSPECTION_INTERVAL_HOURS) * 10) / 10;
  const status = raw.status ?? (raw.available === false ? "grounded" : "available");
  const engineHours = Number.isFinite(raw.engineHours) ? (raw.engineHours as number) : airframeHours;
  const apuHours = Number.isFinite(raw.apuHours) ? (raw.apuHours as number) : 0;
  const parsed = Array.isArray(raw.inspections)
    ? raw.inspections
        .map((row) => hydrateInspection(row, nextInspectionDueHours))
        .filter((row): row is MxInspection => Boolean(row))
    : [];
  const inspections =
    parsed.length > 0
      ? parsed
      : isHawker800xpType(raw.icaoType)
        ? seedHawkerInspections(airframeHours)
        : [];
  return {
    ...raw,
    airframeHours,
    engineHours,
    apuHours,
    cycles: raw.cycles ?? 0,
    nextInspectionDueHours,
    inspections,
    status,
    available: status === "available" && raw.available !== false,
    lastAirportIcao: raw.lastAirportIcao ?? raw.baseIcao ?? "",
    baseIcao: (raw.baseIcao ?? "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4) || undefined,
    fuelOnBoardGal: Number.isFinite(raw.fuelOnBoardGal) ? raw.fuelOnBoardGal : undefined,
    fuelBurnLbHr: Number.isFinite(raw.fuelBurnLbHr) ? raw.fuelBurnLbHr : undefined,
    fuelDensityLbPerGal: Number.isFinite(raw.fuelDensityLbPerGal) ? raw.fuelDensityLbPerGal : undefined,
    fuelUnit: isFuelUnit(raw.fuelUnit) ? raw.fuelUnit : undefined,
    fuelUnitChosen: raw.fuelUnitChosen === true ? true : undefined,
    averageCruiseKt: optionalPerf(raw.averageCruiseKt, 800),
    cruiseMach: optionalPerf(raw.cruiseMach, 2, 2),
    climbRateFpm: optionalPerf(raw.climbRateFpm, 20000),
    descentRateFpm: optionalPerf(raw.descentRateFpm, 20000),
    singlePilot: raw.singlePilot === true ? true : undefined,
    acarsCapable: raw.acarsCapable === "hoppie" ? "hoppie" : undefined,
    engineCount: finiteEngineCount(raw.engineCount),
    engineHoursEach: finiteHourList(raw.engineHoursEach),
  };
}

function finiteEngineCount(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const count = Math.floor(value);
  if (count < 1 || count > 4) return undefined;
  return count;
}

function finiteHourList(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const hours = value.filter((row): row is number => typeof row === "number" && Number.isFinite(row) && row >= 0);
  return hours.length ? hours.map((row) => Math.round(row * 10) / 10) : undefined;
}

/** Installed engine count: saved count, then the engines line, then a published count, else 2. */
export function tailEngineCount(
  spec?: Pick<AircraftSpec, "engineCount" | "engines"> | null,
  published?: number,
): number {
  const explicit = finiteEngineCount(spec?.engineCount);
  if (explicit) return explicit;
  const lead = /^(\d+)\s*[×x]/i.exec(spec?.engines ?? "");
  if (lead) {
    const count = finiteEngineCount(Number(lead[1]));
    if (count) return count;
  }
  const fromSample = finiteEngineCount(published);
  if (fromSample) return fromSample;
  return 2;
}

/**
 * Tenths this flight adds to each engine. A real per-engine list is used as
 * written, including a zero. When only the union tenth is known, every
 * installed engine takes that tenth.
 */
export function flightEngineAdds(
  spec: Pick<AircraftSpec, "engineCount" | "engines"> | null | undefined,
  addedEach: number[] | undefined,
  unionTenth: number,
): number[] | undefined {
  const tenth = (value: number) => Math.round(Math.max(0, value) * 10) / 10;
  const tracked = addedEach?.some((hours) => typeof hours === "number" && hours > 0) ?? false;
  const union = tenth(unionTenth);
  if (!tracked && !(union > 0)) return undefined;
  const count = tailEngineCount(spec, tracked ? addedEach!.length : undefined);
  return Array.from({ length: count }, (_, index) => {
    if (!tracked) return union;
    const slot = addedEach![index];
    return typeof slot === "number" && Number.isFinite(slot) ? tenth(slot) : 0;
  });
}

/**
 * Add this flight's per-engine tenths. The shared engine Hobbs moves by the
 * largest tenth so a twin is not billed twice. A tail with no per-engine
 * record starts each slot from the existing Hobbs.
 */
export function applyEngineMxHours(
  spec: AircraftSpec,
  addedEach: number[] | undefined,
  addedMax: number,
): AircraftSpec {
  const count = tailEngineCount(spec, addedEach?.length);
  const tenth = (value: number) => Math.round(value * 10) / 10;
  const maxAdd = tenth(Math.max(0, addedMax, ...(addedEach ?? [])));
  if (!(maxAdd > 0)) return spec;
  const base = spec.engineHours ?? spec.airframeHours;
  const prior = spec.engineHoursEach?.length ? spec.engineHoursEach : Array.from({ length: count }, () => base);
  const engineHoursEach = Array.from({ length: count }, (_, index) => {
    const add = addedEach && Number.isFinite(addedEach[index]) ? tenth(addedEach[index]!) : maxAdd;
    const start = Number.isFinite(prior[index]) ? prior[index]! : base;
    return tenth(start + add);
  });
  return {
    ...spec,
    engineHours: Math.round(((spec.engineHours ?? spec.airframeHours) + maxAdd) * 100) / 100,
    engineHoursEach,
    engineCount: spec.engineCount ?? count,
  };
}

/**
 * `seed` only fills saves written before the fleet existed. An empty list is the user's choice and stays
 * empty; catalog airframes come back only through Fleet › Add Airframe.
 */
export function hydrateFleet(list: AircraftSpec[] | undefined | null, seed: () => AircraftSpec[]): AircraftSpec[] {
  if (!Array.isArray(list)) return seed();
  return list.map(hydrateAircraft);
}

export function hoursFromTrip(
  mission: Mission,
  log?: FlightLogEntry,
  opts?: {
    userOverrideHours?: number | null;
    simEngineHours?: number | null;
    simAirHours?: number | null;
    ofpPlannedHours?: number | null;
  },
): number {
  const ete = mission.eteMin > 0 ? Math.round((mission.eteMin / 60) * 100) / 100 : null;
  const filed = opts?.userOverrideHours ?? log?.arrival?.engineHours;
  const picked = pickPreferredHours({
    userOverride: filed,
    simActual: opts?.simEngineHours ?? opts?.simAirHours,
    ofpPlanned: opts?.ofpPlannedHours,
    deskEstimate: ete,
  });
  return picked?.value ?? 0;
}

export function applyHoursToTail(
  spec: AircraftSpec,
  hours: number,
  destIcao?: string,
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): AircraftSpec {
  if (hours <= 0) {
    return withResolvedStatus({ ...spec, lastAirportIcao: destIcao ?? spec.lastAirportIcao }, squawks, melItems);
  }
  const next: AircraftSpec = {
    ...spec,
    airframeHours: Math.round((spec.airframeHours + hours) * 100) / 100,
    engineHours: Math.round(((spec.engineHours ?? spec.airframeHours) + hours) * 100) / 100,
    cycles: (spec.cycles ?? 0) + 1,
    lastAirportIcao: destIcao ?? spec.lastAirportIcao,
  };
  return withResolvedStatus(next, squawks, melItems);
}

export function resetInspection(
  spec: AircraftSpec,
  intervalHours: number,
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): AircraftSpec {
  const interval = intervalHours > 0 ? intervalHours : DEFAULT_INSPECTION_INTERVAL_HOURS;
  const airframeHours = spec.airframeHours;
  const inspections = (spec.inspections ?? []).map((item) =>
    item.lastCompletedHours != null || airframeHours >= item.dueHours
      ? {
          ...item,
          dueHours: Math.round((airframeHours + item.intervalHours) * 10) / 10,
          lastCompletedHours: airframeHours,
        }
      : item,
  );
  const next: AircraftSpec = {
    ...spec,
    nextInspectionDueHours: Math.round((airframeHours + interval) * 10) / 10,
    inspections,
    status: "available",
    available: true,
  };
  return withResolvedStatus(next, squawks, melItems);
}

export function upsertInspection(spec: AircraftSpec, item: MxInspection): AircraftSpec {
  const list = spec.inspections ?? [];
  const idx = list.findIndex((i) => i.id === item.id);
  const inspections = idx === -1 ? [...list, item] : list.map((i, n) => (n === idx ? item : i));
  return { ...spec, inspections };
}

export function patchMxTimes(
  spec: AircraftSpec,
  patch: Partial<Pick<AircraftSpec, "airframeHours" | "engineHours" | "apuHours" | "nextInspectionDueHours" | "fuelOnBoardGal">>,
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): AircraftSpec {
  const next: AircraftSpec = {
    ...spec,
    airframeHours: Number.isFinite(patch.airframeHours) ? (patch.airframeHours as number) : spec.airframeHours,
    engineHours: Number.isFinite(patch.engineHours) ? (patch.engineHours as number) : spec.engineHours,
    apuHours: Number.isFinite(patch.apuHours) ? (patch.apuHours as number) : spec.apuHours,
    nextInspectionDueHours: Number.isFinite(patch.nextInspectionDueHours)
      ? (patch.nextInspectionDueHours as number)
      : spec.nextInspectionDueHours,
    fuelOnBoardGal: Number.isFinite(patch.fuelOnBoardGal) ? patch.fuelOnBoardGal : spec.fuelOnBoardGal,
  };
  return withResolvedStatus(next, squawks, melItems);
}

export function newSquawkId(now = appNow()): string {
  return `sqk-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

export function createSquawk(input: {
  aircraft: AircraftSpec;
  severity: SquawkSeverity;
  title: string;
  detail?: string;
  role: DeskRole;
  now?: Date;
}): Squawk {
  const now = input.now ?? appNow();
  return {
    id: newSquawkId(now),
    aircraftId: input.aircraft.id,
    registration: input.aircraft.registration,
    severity: input.severity,
    status: "open",
    title: input.title.trim() || "Squawk",
    detail: (input.detail ?? "").trim(),
    createdAtUtc: now.toISOString(),
    createdBy: input.role,
  };
}

export function closeSquawk(squawk: Squawk, now = appNow()): Squawk {
  return { ...squawk, status: "closed", closedAtUtc: now.toISOString() };
}

export function closeGroundSquawks(aircraftId: string, squawks: Squawk[], now = appNow()): Squawk[] {
  return squawks.map((s) =>
    s.aircraftId === aircraftId && s.status === "open" && s.severity === "ground" ? closeSquawk(s, now) : s,
  );
}

function ferryCallsign(spec: AircraftSpec): string {
  return radioIdFor(spec);
}

/**
 * Empty company ferry to an MRO. When the sector is a realistic paying charter
 * (see `findMxPayloadOpportunity` / `canCarryMxPayload`), Inbox prefers a
 * revenue RFQ on that positioning leg instead of only this empty move.
 */
export function buildFerryDraft(
  spec: AircraftSpec,
  mxBase: CompanyMxBase,
  scenery: SceneryAirport[],
  homeIcao = "",
  now = appNow(),
): { ok: true; draft: DraftMission } | { ok: false; error: string } {
  const originIcao = (spec.lastAirportIcao || homeIcao).trim().toUpperCase();
  const destIcao = mxBase.icao.toUpperCase();
  if (!originIcao) {
    return { ok: false, error: `${spec.registration} has no origin. Set the tail base or company home.` };
  }
  if (originIcao === destIcao) {
    return { ok: false, error: `${spec.registration} is already at ${destIcao}. Close Mx here, or move the tail first.` };
  }
  const origin = hydrateSceneryAirport(originIcao, scenery);
  const dest = hydrateSceneryAirport(destIcao, scenery);
  if (!origin || !dest) {
    const missing = !origin ? originIcao : destIcao;
    const rec = lookupAirport(missing);
    if (!rec) return { ok: false, error: `No coordinates for ${missing}. Add it on Destinations.` };
  }
  const o = origin ?? hydrateSceneryAirport(originIcao, scenery);
  const d = dest ?? hydrateSceneryAirport(destIcao, scenery);
  if (!o || !d) return { ok: false, error: "Could not resolve ferry airports." };

  const departure = suggestDeparture(now);
  const shop = mxBase.description.trim() || mxBase.icao;
  const purpose = `Ferry to mx — ${mxBase.icao}`;
  const leg = computeLeg(1, o, d, spec, departure, 0, [], purpose);
  if (!leg) return { ok: false, error: "Could not compute the ferry leg." };

  const draft: DraftMission = withLegs({
    id: `mx-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`,
    createdAt: now.toISOString(),
    callsign: ferryCallsign(spec),
    aircraftId: spec.id,
    aircraftName: spec.name,
    registration: spec.registration,
    flightType: "ferry",
    originIcao: o.icao,
    destIcao: d.icao,
    originName: o.name,
    destName: d.name,
    originCity: o.city,
    destCity: d.city,
    distanceNm: leg.distanceNm,
    eteMin: leg.eteMin,
    fuelGal: leg.fuelGal,
    fuelLbs: leg.fuelLbs,
    trackTrue: leg.trackTrue,
    paxCount: 0,
    paxNames: [],
    scenario: `No charter revenue. ${spec.registration} ferry ${o.icao}–${d.icao}. ${shop}`,
    purpose,
    departureIso: departure.toISOString(),
    weatherNote: "Mx ferry — company MRO",
    originMetar: leg.originMetar,
    destMetar: leg.destMetar,
    isMaintenance: true,
    serviceLabel: shop,
    originRunwayFt: o.runwayFt,
    destRunwayFt: d.runwayFt,
    legs: [leg],
    source: "manual",
    mroFacilityId: mxBase.id,
  });
  return { ok: true, draft };
}

export const AIRFRAME_STATUS_LABEL: Record<AirframeStatus, string> = {
  available: "Available",
  grounded: "Grounded",
  ferry: "Ferry",
  in_mx: "In mx",
};

export const SQUAWK_SEVERITY_LABEL: Record<SquawkSeverity, string> = {
  info: "Info",
  defer: "Defer",
  ground: "Ground",
};
